// src/services/TranslationService.js
const MYMEMORY_API = 'https://api.mymemory.translated.net/get';
const translationCache = new Map();

// 👇 AÑADE TU EMAIL AQUÍ PARA 10,000 PALABRAS/DÍA
const REGISTERED_EMAIL = 'fiveat@whatalab.io';

const getCacheKey = (text, sourceLang, targetLang) => {
  return `${sourceLang}|${targetLang}|${text.substring(0, 50)}`;
};

const delay = (ms) => new Promise(r => setTimeout(r, ms));

export const translateText = async (text, sourceLang = 'es', targetLang = 'en', retries = 3) => {
  if (!text || text.trim() === '') return '';
  
  const cacheKey = getCacheKey(text, sourceLang, targetLang);
  if (translationCache.has(cacheKey)) {
    console.log('[Translation] ✓ Cache hit');
    return translationCache.get(cacheKey);
  }

  let lastError = null;
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`[Translation] Intento ${attempt}/${retries}: "${text.substring(0, 30)}..."`);
      
      const url = `${MYMEMORY_API}?q=${encodeURIComponent(text)}&langpair=${sourceLang}|${targetLang}&de=${REGISTERED_EMAIL}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Verificar respuesta válida
      if (data.responseStatus === 200 && data.responseData && data.responseData.translatedText) {
        const translatedText = data.responseData.translatedText;
        
        // Detectar si NO tradujo (devolvió lo mismo o en mayúsculas)
        if (translatedText.toLowerCase() === text.toLowerCase()) {
          console.warn(`[Translation] ⚠️ API devolvió texto sin traducir`);
          if (attempt < retries) {
            await delay(1000 * attempt); // Esperar más en cada intento
            continue;
          }
        }
        
        // Detectar errores de cuota
        if (translatedText.includes('MYMEMORY WARNING') || 
            translatedText.includes('PLEASE CONTACT') ||
            translatedText.includes('LIMIT')) {
          console.error('[Translation] ❌ Límite de API alcanzado');
          throw new Error('Límite de traducción alcanzado. Intenta más tarde.');
        }

        console.log(`[Translation] ✓ OK: "${translatedText.substring(0, 30)}..."`);
        translationCache.set(cacheKey, translatedText);
        return translatedText;
      } else {
        // API respondió pero con error
        const errorMsg = data.responseDetails || 'Respuesta inválida';
        console.warn(`[Translation] ⚠️ API error: ${errorMsg}`);
        throw new Error(errorMsg);
      }
    } catch (error) {
      lastError = error;
      console.error(`[Translation] ❌ Intento ${attempt} falló:`, error.message);
      
      if (attempt < retries) {
        const waitTime = 1000 * attempt;
        console.log(`[Translation] Esperando ${waitTime}ms antes de reintentar...`);
        await delay(waitTime);
      }
    }
  }

  // Todos los intentos fallaron
  console.error('[Translation] ❌ FALLÓ después de todos los intentos');
  throw lastError || new Error('Traducción falló');
};

export const translateNewsItem = async (newsItem, sourceLang = 'es', targetLang = 'en') => {
  const translated = { ...newsItem };
  const errors = [];

  // Traducir título
  if (newsItem.title) {
    try {
      translated.title = await translateText(newsItem.title, sourceLang, targetLang);
    } catch (err) {
      errors.push(`Título: ${err.message}`);
      translated.title = newsItem.title; // Mantener original si falla
    }
  }

  await delay(300);

  // Traducir cuerpo
  if (newsItem.body) {
    try {
      translated.body = await translateText(newsItem.body, sourceLang, targetLang);
    } catch (err) {
      errors.push(`Cuerpo: ${err.message}`);
      translated.body = newsItem.body; // Mantener original si falla
    }
  }

  await delay(300);

  // Traducir texto del enlace
  if (newsItem.urlText) {
    try {
      translated.urlText = await translateText(newsItem.urlText, sourceLang, targetLang);
    } catch (err) {
      translated.urlText = newsItem.urlText;
    }
  }

  // Marcar si hubo errores
  if (errors.length > 0) {
    translated._errors = errors;
  }

  return translated;
};

export const translateMultiNews = async (newsArray, sourceLang = 'es', targetLang = 'en', onProgress = null) => {
  const results = [];
  const validNews = newsArray.filter(n => n.title && n.body);
  const total = validNews.length;
  let current = 0;
  let totalErrors = [];

  for (const news of newsArray) {
    if (!news.title || !news.body) {
      results.push({ ...news });
      continue;
    }

    if (onProgress) {
      onProgress(current, total, `Traduciendo noticia ${current + 1} de ${total}...`);
    }

    const translated = await translateNewsItem(news, sourceLang, targetLang);
    
    // Recopilar errores
    if (translated._errors) {
      totalErrors.push(`Noticia ${current + 1}: ${translated._errors.join(', ')}`);
      delete translated._errors;
    }
    
    results.push(translated);
    current++;

    await delay(500);
  }

  if (onProgress) {
    if (totalErrors.length > 0) {
      onProgress(total, total, `⚠️ Completado con ${totalErrors.length} errores`);
    } else {
      onProgress(total, total, '✓ ¡Traducción completada!');
    }
  }

  // Adjuntar errores al resultado para que el componente pueda mostrarlos
  results._translationErrors = totalErrors;

  return results;
};

export const translatePostMeta = async (postData, sourceLang = 'es', targetLang = 'en') => {
  const translated = {};
  const errors = [];

  if (postData.title) {
    try {
      translated.title = await translateText(postData.title, sourceLang, targetLang);
    } catch (err) {
      errors.push(`Título: ${err.message}`);
      translated.title = postData.title;
    }
  }

  await delay(300);

  if (postData.excerpt) {
    try {
      translated.excerpt = await translateText(postData.excerpt, sourceLang, targetLang);
    } catch (err) {
      errors.push(`Extracto: ${err.message}`);
      translated.excerpt = postData.excerpt;
    }
  }

  translated._errors = errors;
  return translated;
};

export const clearTranslationCache = () => translationCache.clear();